import { Lazy } from '../chat/lazy';
import { fromIsoString, toIsoString } from '../chat/iso-date';
import { EraQL, Parser, TC, Ty } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { v4 } from 'uuid';
import type {
  WorkflowData,
  SerializedWorkflow,
  SerializedWorkflowCronTrigger,
  SerializedWorkflowEdge,
  SerializedWorkflowNode,
  SerializedWorkflowTriggerBinding,
  SerializedWorkflowWebhookTrigger,
  SerializedWorkflowDatasetTrigger,
  SerializedWorkflowEventTrigger,
  WorkflowResourceGraphNode,
  WorkflowResourceGraphEdge,
  WorkflowResourceGraphTriggerBinding,
  WorkflowResourceGraphTriggerSnapshot,
  DraftNodeParams,
  DraftAgentNodeParams,
  DraftForEachChildNodeParams,
  DraftForEachNodeParams,
  DraftAdHocAgentNodeParams,
  DraftCompleteToolCallNodeParams,
  DraftToolCallNodeParams,
  DraftCronTriggerParams,
  DraftWebhookTriggerParams,
  DraftEventTriggerParams,
  DraftEdgeParams,
} from './types';
import { WorkflowNodeType, WorkflowRunStepStatus } from './types';
import type { WorkflowEdge } from './workflow-edge';
import type { WorkflowNode } from './workflow-node';
import type { WorkflowTrigger } from './workflow-trigger';
import type { WorkflowTriggerBinding } from './workflow-trigger-binding';
import { WorkflowCronTrigger } from './workflow-cron-trigger';
import { WorkflowEdge as WorkflowEdgeModel } from './workflow-edge';
import { WorkflowNode as WorkflowNodeModel } from './workflow-node';
import { WorkflowTriggerBinding as WorkflowTriggerBindingModel } from './workflow-trigger-binding';
import { WorkflowWebhookTrigger } from './workflow-webhook-trigger';
import { WorkflowDatasetTrigger } from './workflow-dataset-trigger';
import { WorkflowEventTrigger } from './workflow-event-trigger';
import { JsonValue } from '../serializable';
import {
  parseWorkflowIdempotencyKey,
  resolveWorkflowIdempotencyKey,
  type ResolveWorkflowIdempotencyKeyResult,
  type WorkflowIdempotencyKey,
} from './workflow-idempotency-key';
import { WorkflowGraph } from './workflow-graph';
import {
  WorkflowRunInputSchema,
  type ValidateWorkflowRunInputResult,
  type WorkflowRunInputSchemaBundle,
} from './workflow-run-input';

export type WorkflowHydration = {
  readonly loadNodes: () => Promise<readonly WorkflowNode[]>;
  readonly loadEdges: () => Promise<readonly WorkflowEdge[]>;
  readonly loadTriggerBindings: () => Promise<
    readonly WorkflowTriggerBinding[]
  >;
  readonly loadTriggers?: () => Promise<readonly WorkflowTrigger[]>;
  readonly preloadedNodes?: readonly WorkflowNode[];
  readonly preloadedEdges?: readonly WorkflowEdge[];
  readonly preloadedTriggerBindings?: readonly WorkflowTriggerBinding[];
  readonly preloadedTriggers?: readonly WorkflowTrigger[];
};

export type WorkflowValidationIssue =
  | {
      readonly code: 'UnknownNodeReference';
      readonly edgeId: string;
      readonly nodeId: string;
      readonly message: string;
    }
  | {
      readonly code: 'InvalidInputShape';
      readonly nodeId: string;
      readonly inputShape: string;
      readonly message: string;
    }
  | {
      readonly code: 'MissingIncomingOutput';
      readonly nodeId: string;
      readonly sourceNodeId: string;
      readonly edgeId: string;
      readonly message: string;
    }
  | {
      readonly code: 'InvalidSourceDefaultOutput';
      readonly nodeId: string;
      readonly sourceNodeId: string;
      readonly edgeId: string;
      readonly message: string;
    }
  | {
      readonly code: 'InputOutputMismatch';
      readonly nodeId: string;
      readonly sourceNodeId: string;
      readonly edgeId: string;
      readonly inputShape: string;
      readonly sourceOutputShape: string;
      readonly message: string;
    }
  | {
      readonly code: 'MissingWebhookOutput';
      readonly nodeId: string;
      readonly triggerId: string;
      readonly message: string;
    }
  | {
      readonly code: 'WebhookInputMismatch';
      readonly nodeId: string;
      readonly triggerId: string;
      readonly inputShape: string;
      readonly webhookOutputShape: string;
      readonly message: string;
    };

export type WebhookTriggerOutputContext = {
  readonly triggerId: string;
  readonly sampleRunInput: unknown;
  readonly runOutputShape: string;
};

export type WorkflowValidateOptions = {
  readonly webhookTriggerOutputs?: readonly WebhookTriggerOutputContext[];
};

export type WorkflowValidationResult = {
  readonly valid: boolean;
  readonly issues: readonly WorkflowValidationIssue[];
};

export enum PersistedWorkflowTriggerKind {
  Cron = 'cron',
  Webhook = 'webhook',
  Event = 'event',
  Dataset = 'dataset',
}

export type PersistedWorkflowTriggerSnapshot =
  | {
      readonly kind: PersistedWorkflowTriggerKind.Cron;
      readonly trigger: SerializedWorkflowCronTrigger;
    }
  | {
      readonly kind: PersistedWorkflowTriggerKind.Webhook;
      readonly trigger: SerializedWorkflowWebhookTrigger;
    }
  | {
      readonly kind: PersistedWorkflowTriggerKind.Event;
      readonly trigger: SerializedWorkflowEventTrigger;
    }
  | {
      readonly kind: PersistedWorkflowTriggerKind.Dataset;
      readonly trigger: SerializedWorkflowDatasetTrigger;
    };

export type PersistedWorkflowSnapshot = {
  readonly workflow: SerializedWorkflow;
  readonly nodes: readonly SerializedWorkflowNode[];
  readonly edges: readonly SerializedWorkflowEdge[];
  readonly triggerBindings: readonly SerializedWorkflowTriggerBinding[];
  readonly triggers: readonly PersistedWorkflowTriggerSnapshot[];
};

export type WorkflowResourceGraph = {
  readonly nodes: ReadonlyArray<WorkflowResourceGraphNode>;
  readonly edges: ReadonlyArray<WorkflowResourceGraphEdge>;
  readonly triggerBindings: ReadonlyArray<WorkflowResourceGraphTriggerBinding>;
  readonly triggers: ReadonlyArray<WorkflowResourceGraphTriggerSnapshot>;
};

export type WorkflowResource = {
  readonly id: string;
  readonly ownerId: string;
  readonly orgId: string;
  readonly name: string;
  readonly workflowStatus?: string;
  readonly workflowType?: string;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly graph?: WorkflowResourceGraph;
};

type DraftCollections = {
  readonly nodes: WorkflowNode[];
  readonly edges: WorkflowEdge[];
  readonly triggerBindings: WorkflowTriggerBinding[];
  readonly triggers: WorkflowTrigger[];
};

export type WorkflowRunNodeDefinition = {
  readonly runNodeId: string;
  readonly type?: string;
  /** Run node whose output supplies this step's input (when set). */
  readonly inputFromRunNodeId?: string;
};

export type WorkflowRunInstanceSnapshot = {
  readonly instanceKey: string;
  readonly parentInstanceKey: string | null;
  readonly status: WorkflowRunStepStatus;
};

export type WorkflowRunStepSnapshot = {
  readonly runNodeId: string;
  readonly statusForInstance?: WorkflowRunStepStatus;
  readonly instances: readonly WorkflowRunInstanceSnapshot[];
  readonly nodeDefinition: WorkflowRunNodeDefinition;
};

export type WorkflowRunSnapshot = {
  readonly runId: string;
  readonly steps: readonly WorkflowRunStepSnapshot[];
  readonly edges: ReadonlyArray<{ fromRunNodeId: string; toRunNodeId: string }>;
};

class WorkflowRunInstanceView {
  constructor(private readonly _snapshot: WorkflowRunInstanceSnapshot) {}

  get instanceKey(): string {
    return this._snapshot.instanceKey;
  }

  get parentInstanceKey(): string | null {
    return this._snapshot.parentInstanceKey;
  }

  get status(): WorkflowRunStepStatus {
    return this._snapshot.status;
  }

  isComplete(): boolean {
    return this._snapshot.status === WorkflowRunStepStatus.Completed;
  }

  /**
   * Whether a fan-out (for-each) item has finished without failing: it either
   * completed, or its child workflow was deliberately stopped by a gate. Like a
   * bucket's `Absorbed`, `Stopped` is settled and non-failing without being
   * `Completed`, so it lets the loop continue but never reads as completed.
   */
  isSettledFanOutItem(): boolean {
    return (
      this._snapshot.status === WorkflowRunStepStatus.Completed ||
      this._snapshot.status === WorkflowRunStepStatus.Stopped
    );
  }
}

class WorkflowRunStepView {
  constructor(private readonly _snapshot: WorkflowRunStepSnapshot) {}

  get runNodeId(): string {
    return this._snapshot.runNodeId;
  }

  nodeDefinition(): WorkflowRunNodeDefinition {
    return this._snapshot.nodeDefinition;
  }

  statusForInstance(): WorkflowRunStepStatus | undefined {
    return this._snapshot.statusForInstance;
  }

  instances(): WorkflowRunInstanceView[] {
    return this._snapshot.instances.map(
      (instance) => new WorkflowRunInstanceView(instance)
    );
  }

  hasIncompleteInstances(): boolean {
    return this._snapshot.instances.some(
      (instance) => instance.status !== WorkflowRunStepStatus.Completed
    );
  }

  childInstancesForParent(
    parentInstanceKey: string
  ): WorkflowRunInstanceView[] {
    return this.instances().filter(
      (instance) => instance.parentInstanceKey === parentInstanceKey
    );
  }
}

class WorkflowRunView {
  private readonly stepsByRunNodeId: Map<string, WorkflowRunStepView>;
  private readonly incomingByNodeId: Map<string, string[]>;

  constructor(private readonly _snapshot: WorkflowRunSnapshot) {
    this.stepsByRunNodeId = new Map(
      _snapshot.steps.map((step) => [
        step.runNodeId,
        new WorkflowRunStepView(step),
      ])
    );
    this.incomingByNodeId = new Map();
    for (const edge of _snapshot.edges) {
      const existing = this.incomingByNodeId.get(edge.toRunNodeId) ?? [];
      existing.push(edge.fromRunNodeId);
      this.incomingByNodeId.set(edge.toRunNodeId, existing);
    }
  }

  steps(): WorkflowRunStepView[] {
    return this._snapshot.steps.map(
      (step) => this.stepsByRunNodeId.get(step.runNodeId)!
    );
  }

  instances(): WorkflowRunInstanceView[] {
    return this.steps().flatMap((step) => step.instances());
  }

  getNextNodeIds(params: {
    instanceKey: string;
    resumeAtRunNodeId?: string;
    parentInstanceKey?: string;
  }): string[] {
    const isChildInstanceExecution =
      params.parentInstanceKey !== undefined &&
      params.parentInstanceKey !== params.instanceKey;
    const allRunNodeIds = this._snapshot.steps.map((step) => step.runNodeId);
    const candidateRunNodeIds =
      params.resumeAtRunNodeId !== undefined
        ? allRunNodeIds.includes(params.resumeAtRunNodeId)
          ? [params.resumeAtRunNodeId]
          : []
        : isChildInstanceExecution
        ? []
        : allRunNodeIds.filter((runNodeId) => {
            const step = this.stepsByRunNodeId.get(runNodeId);
            return step?.statusForInstance() === undefined;
          });

    // Branches a filter deliberately halted can never run. Fan-in nodes join
    // with "at least one" readiness, so they must know which of their
    // predecessors were filtered out (won't arrive) versus still pending or
    // failed.
    const filteredOutRunNodeIds = computeFilteredOutRunNodeIds(
      this.stepsByRunNodeId,
      this.incomingByNodeId,
      params.instanceKey
    );

    return candidateRunNodeIds
      .sort((left, right) => left.localeCompare(right))
      .filter((candidateRunNodeId) => {
        if (filteredOutRunNodeIds.has(candidateRunNodeId)) {
          return false;
        }
        const predecessorIds =
          this.incomingByNodeId.get(candidateRunNodeId) ?? [];
        const step = this.stepsByRunNodeId.get(candidateRunNodeId);
        if (!isChildInstanceExecution && step !== undefined) {
          const fanOutChildInstances = step.childInstancesForParent(
            params.instanceKey
          );
          if (
            fanOutChildInstances.length > 0 &&
            fanOutChildInstances.every((instance) =>
              instance.isSettledFanOutItem()
            )
          ) {
            return false;
          }
        }
        // Fan-in nodes (more than one incoming branch) join with "at least one"
        // readiness: ready once every incoming branch has settled (completed or
        // filtered out by a filter) and at least one completed. A filtered-out
        // branch never blocks the join (a strict all-inputs wait would hang on it
        // forever); a failed/cancelled branch is NOT skippable — it blocks the
        // join so the failure fails the run. For-each aggregation keeps the
        // existing strict-satisfaction path (its Incomplete state is special).
        const hasForEachPredecessor = predecessorIds.some(
          (predecessorRunNodeId) =>
            this.stepsByRunNodeId.get(predecessorRunNodeId)?.nodeDefinition()
              .type === WorkflowNodeType.ForEach
        );
        if (
          predecessorIds.length > 1 &&
          !isChildInstanceExecution &&
          !hasForEachPredecessor
        ) {
          return isFanInRunNodeReady({
            runNodeId: candidateRunNodeId,
            stepsByRunNodeId: this.stepsByRunNodeId,
            incomingByNodeId: this.incomingByNodeId,
            instanceKey: params.instanceKey,
            filteredOutRunNodeIds,
          });
        }
        const inputFromRunNodeId = step?.nodeDefinition().inputFromRunNodeId;
        if (
          inputFromRunNodeId !== undefined &&
          !isRunPredecessorSatisfied({
            predecessorRunNodeId: inputFromRunNodeId,
            stepsByRunNodeId: this.stepsByRunNodeId,
            instanceKey: params.instanceKey,
            isChildInstanceExecution,
          })
        ) {
          return false;
        }

        for (const predecessorRunNodeId of predecessorIds) {
          if (
            !isRunPredecessorSatisfied({
              predecessorRunNodeId,
              stepsByRunNodeId: this.stepsByRunNodeId,
              instanceKey: params.instanceKey,
              isChildInstanceExecution,
            })
          ) {
            return false;
          }
        }
        return true;
      });
  }
}

/** Same-instance status of a run node, or `undefined` if it hasn't run. */
const sameInstanceRunStatus = (
  runNodeId: string,
  stepsByRunNodeId: Map<string, WorkflowRunStepView>,
  instanceKey: string
): WorkflowRunStepStatus | undefined => {
  const step = stepsByRunNodeId.get(runNodeId);
  if (step === undefined) {
    return undefined;
  }
  return step
    .instances()
    .find((instance) => instance.instanceKey === instanceKey)?.status;
};

/**
 * Whether a run node was "filtered out": deliberately gated off by a filter.
 * That means its own step `Stopped`/`Absorbed`, or it is transitively downstream
 * of one (in {@link computeFilteredOutRunNodeIds}). These are the ONLY states a
 * fan-in join treats as a skippable branch. It deliberately excludes
 * `Failed`/`Cancelled` — a failed branch must still block/fail the run, not be
 * coalesced away by a sibling. `Absorbed` (a bucket collecting the run) is a
 * deliberate halt like `Stopped`: successors run only in the bucket's flush run.
 */
const isRunNodeFilteredOut = (
  runNodeId: string,
  stepsByRunNodeId: Map<string, WorkflowRunStepView>,
  instanceKey: string,
  filteredOutRunNodeIds: ReadonlySet<string>
): boolean => {
  if (filteredOutRunNodeIds.has(runNodeId)) {
    return true;
  }
  const status = sameInstanceRunStatus(
    runNodeId,
    stepsByRunNodeId,
    instanceKey
  );
  return (
    status === WorkflowRunStepStatus.Stopped ||
    status === WorkflowRunStepStatus.Absorbed
  );
};

/**
 * Run nodes that can never execute for this instance because a filter upstream
 * halted their only path. Propagates forward to a fixpoint from `Stopped`
 * filters only (NOT failures/cancellations). A node is filtered out only when
 * EVERY incoming branch is filtered out — a single surviving (completed or still
 * live) branch keeps it alive, so a fan-in whose one branch was gated off still
 * runs. Completed nodes are never filtered out.
 */
const computeFilteredOutRunNodeIds = (
  stepsByRunNodeId: Map<string, WorkflowRunStepView>,
  incomingByNodeId: Map<string, string[]>,
  instanceKey: string
): Set<string> => {
  const filteredOut = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const runNodeId of stepsByRunNodeId.keys()) {
      if (filteredOut.has(runNodeId)) {
        continue;
      }
      const status = sameInstanceRunStatus(
        runNodeId,
        stepsByRunNodeId,
        instanceKey
      );
      if (status === WorkflowRunStepStatus.Completed) {
        continue;
      }
      const predecessorIds = incomingByNodeId.get(runNodeId) ?? [];
      if (predecessorIds.length === 0) {
        // Entry / run-input nodes have no predecessors and are never filtered out.
        continue;
      }
      const nowFilteredOut = predecessorIds.every((predecessorRunNodeId) =>
        isRunNodeFilteredOut(
          predecessorRunNodeId,
          stepsByRunNodeId,
          instanceKey,
          filteredOut
        )
      );
      if (nowFilteredOut) {
        filteredOut.add(runNodeId);
        changed = true;
      }
    }
  }
  return filteredOut;
};

/**
 * Whether a fan-in node is ready to run: every incoming branch has settled to a
 * skippable state (completed, or filtered out by a filter) and at least one
 * completed. A still-pending — OR failed/cancelled — predecessor returns false:
 * pending means "wait", and a failed/cancelled branch must block the join so the
 * failure surfaces on the run instead of being coalesced away.
 */
const isFanInRunNodeReady = (params: {
  runNodeId: string;
  stepsByRunNodeId: Map<string, WorkflowRunStepView>;
  incomingByNodeId: Map<string, string[]>;
  instanceKey: string;
  filteredOutRunNodeIds: ReadonlySet<string>;
}): boolean => {
  const predecessorIds = params.incomingByNodeId.get(params.runNodeId) ?? [];
  if (predecessorIds.length === 0) {
    return false;
  }
  let anyCompleted = false;
  for (const predecessorRunNodeId of predecessorIds) {
    const status = sameInstanceRunStatus(
      predecessorRunNodeId,
      params.stepsByRunNodeId,
      params.instanceKey
    );
    if (status === WorkflowRunStepStatus.Completed) {
      anyCompleted = true;
      continue;
    }
    if (
      isRunNodeFilteredOut(
        predecessorRunNodeId,
        params.stepsByRunNodeId,
        params.instanceKey,
        params.filteredOutRunNodeIds
      )
    ) {
      continue;
    }
    // Still live (queued/running), or failed/cancelled — either way the merge is
    // not ready: wait for a live branch, or let the failure fail the run.
    return false;
  }
  return anyCompleted;
};

const isRunPredecessorSatisfied = (params: {
  predecessorRunNodeId: string;
  stepsByRunNodeId: Map<string, WorkflowRunStepView>;
  instanceKey: string;
  isChildInstanceExecution: boolean;
}): boolean => {
  const predecessorStep = params.stepsByRunNodeId.get(
    params.predecessorRunNodeId
  );
  if (predecessorStep === undefined) {
    return false;
  }
  const predecessorType = predecessorStep.nodeDefinition().type;
  const predecessorInstances = predecessorStep.instances();
  const hasIncompletePredecessorInstance =
    predecessorStep.hasIncompleteInstances();
  const fanOutChildInstances = predecessorStep.childInstancesForParent(
    params.instanceKey
  );
  if (fanOutChildInstances.length > 0) {
    const hasUnsettledFanOutChild = fanOutChildInstances.some(
      (instance) => !instance.isSettledFanOutItem()
    );
    // This fan-out's own items are judged by `isSettledFanOutItem` above (a
    // stopped item is settled); every other instance must still be complete.
    const hasIncompleteOtherInstance = predecessorInstances.some(
      (instance) =>
        instance.parentInstanceKey !== params.instanceKey &&
        !instance.isComplete()
    );
    if (hasUnsettledFanOutChild || hasIncompleteOtherInstance) {
      return false;
    }
    if (!params.isChildInstanceExecution) {
      const sameInstancePredecessor = predecessorInstances.find(
        (instance) => instance.instanceKey === params.instanceKey
      );
      return (
        sameInstancePredecessor?.status === WorkflowRunStepStatus.Completed
      );
    }
    return true;
  }
  const sameInstancePredecessor = predecessorInstances.find(
    (instance) => instance.instanceKey === params.instanceKey
  );
  if (sameInstancePredecessor === undefined) {
    return false;
  }
  const satisfied =
    sameInstancePredecessor.status === WorkflowRunStepStatus.Completed ||
    (predecessorType === WorkflowNodeType.ForEach &&
      sameInstancePredecessor.status === WorkflowRunStepStatus.Incomplete);
  if (!satisfied) {
    return false;
  }
  if (
    sameInstancePredecessor.status === WorkflowRunStepStatus.Completed &&
    hasIncompletePredecessorInstance
  ) {
    return false;
  }
  return true;
};

/**
 * Checks whether a webhook run input provides every field required by a node
 * input shape. Unlike strict {@link TC.implementsTy}, extra webhook fields such
 * as `webhookPayload` are allowed.
 */
const providerOutputSatisfiesInput = (params: {
  subject: Ty.ExtendedAttributeType;
  req: Ty.ExtendedAttributeType;
}): boolean => {
  if (params.req.ty.k !== 'struct' || params.subject.ty.k !== 'struct') {
    return TC.implementsTy({
      subject: params.subject,
      req: params.req,
    });
  }

  for (const [reqName, reqTy] of Object.entries(params.req.ty.fields)) {
    const subTy = params.subject.ty.fields[reqName];
    if (subTy === undefined) {
      if (!reqTy.nullable) {
        return false;
      }
      continue;
    }
    if (
      !TC.implementsTy({
        subject: subTy,
        req: reqTy,
      })
    ) {
      return false;
    }
  }

  return true;
};

export type ScheduledAgentData = {
  readonly cronTriggerId: string;
  readonly agentNodeId: string;
  readonly schedule: string;
  readonly timezone: string;
  readonly agentId: string;
  readonly prompt: string | null;
};

export class ScheduledAgentWorkflow {
  private readonly _workflow: Workflow;
  private readonly _data: ScheduledAgentData;

  constructor(workflow: Workflow, data: ScheduledAgentData) {
    this._workflow = workflow;
    this._data = data;
  }

  get workflow(): Workflow {
    return this._workflow;
  }

  get data(): ScheduledAgentData {
    return { ...this._data };
  }
}

/**
 * Top-level workflow definition (`workflow_workflows` + graph via lazy loads).
 */
export class Workflow {
  private readonly _data: WorkflowData;
  readonly nodes: Lazy<readonly WorkflowNode[]>;
  readonly edges: Lazy<readonly WorkflowEdge[]>;
  readonly triggerBindings: Lazy<readonly WorkflowTriggerBinding[]>;
  private readonly triggerList: Lazy<readonly WorkflowTrigger[]>;
  private readonly draftCollections?: DraftCollections;

  private constructor(
    data: WorkflowData,
    hydration: WorkflowHydration,
    draftCollections?: DraftCollections
  ) {
    this._data = data;
    this.nodes = new Lazy(hydration.loadNodes, hydration.preloadedNodes);
    this.edges = new Lazy(hydration.loadEdges, hydration.preloadedEdges);
    this.triggerBindings = new Lazy(
      hydration.loadTriggerBindings,
      hydration.preloadedTriggerBindings
    );
    this.triggerList = new Lazy(
      hydration.loadTriggers ?? resolveEmptyWorkflowTriggers,
      hydration.preloadedTriggers
    );
    this.draftCollections = draftCollections;
  }

  get id(): string {
    return this._data.id;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get ownerOrgId(): string {
    return this._data.ownerOrgId;
  }

  get name(): string {
    return this._data.name;
  }

  get status(): string {
    return this._data.status;
  }

  get idempotencyKey(): WorkflowIdempotencyKey | null {
    return this._data.idempotencyKey;
  }

  resolveIdempotencyKey(inputs: unknown): ResolveWorkflowIdempotencyKeyResult {
    return resolveWorkflowIdempotencyKey({
      config: this._data.idempotencyKey,
      inputs,
    });
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  get workflowData(): WorkflowData {
    return { ...this._data };
  }

  run(
    runId: string,
    snapshot: Omit<WorkflowRunSnapshot, 'runId'>
  ): WorkflowRunView {
    return new WorkflowRunView({
      runId,
      steps: snapshot.steps,
      edges: snapshot.edges,
    });
  }

  private static asRecord(value: unknown): Record<string, unknown> {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    return {};
  }

  private static extractPrompt(workflowInput: unknown): string | null {
    const output = Workflow.asRecord(workflowInput);
    const prompt = output['prompt'];
    return typeof prompt === 'string' ? prompt : null;
  }

  async triggers(): Promise<readonly WorkflowTrigger[]> {
    return this.triggerList.get();
  }

  async toScheduledAgent(): Promise<ScheduledAgentWorkflow | null> {
    const [nodes, edges, triggerBindings, triggers] = await Promise.all([
      this.nodes.get(),
      this.edges.get(),
      this.triggerBindings.get(),
      this.triggers(),
    ]);
    const cronBinding = triggerBindings.find(
      (binding) => binding.triggerType === PersistedWorkflowTriggerKind.Cron
    );
    if (cronBinding === undefined) {
      return null;
    }
    const cronTrigger = triggers.find(
      (trigger) =>
        trigger.isCronTrigger() && trigger.id === cronBinding.triggerId
    );
    if (cronTrigger === undefined || !cronTrigger.isCronTrigger()) {
      return null;
    }
    void edges;
    const agentNodes = nodes.filter(
      (node) => node.type === WorkflowNodeType.Agent
    );
    if (agentNodes.length !== 1) {
      return null;
    }
    const agentNode = agentNodes[0];
    if (agentNode === undefined) {
      return null;
    }
    const targetData = Workflow.asRecord(agentNode.data);
    const agentId = targetData['agentId'];
    if (typeof agentId !== 'string') {
      return null;
    }
    return new ScheduledAgentWorkflow(this, {
      cronTriggerId: cronBinding.triggerId,
      agentNodeId: agentNode.id,
      schedule: cronTrigger.cron,
      timezone: cronTrigger.timezone,
      agentId,
      prompt: Workflow.extractPrompt(cronTrigger.workflowInput),
    });
  }

  async isScheduledAgent(): Promise<ScheduledAgentWorkflow | null> {
    return this.toScheduledAgent();
  }

  /** Definition graph view for entry-node / run-input resolution. */
  async graph(): Promise<WorkflowGraph> {
    const [nodes, edges] = await Promise.all([
      this.nodes.get(),
      this.edges.get(),
    ]);
    return WorkflowGraph.fromDomain(nodes, edges);
  }

  /** Definition node ids with no incoming edges (manual run entrypoints). */
  async entryNodeIds(): Promise<string[]> {
    return (await this.graph()).entryNodeIds();
  }

  /**
   * ERA type for manual workflow run input (entry-node `input_shape` values).
   * Mirrors agent `editorState.input()` for workflow graphs.
   */
  async input(): Promise<Ty.ExtendedAttributeType> {
    return (await this.graph()).inputTy();
  }

  /** Validates a manual run payload against entry-node input shapes. */
  async validateRunInput(
    runInput: unknown
  ): Promise<ValidateWorkflowRunInputResult> {
    return (await this.inputSchema()).validate(runInput);
  }

  /** Resolved schema + validation for manual workflow run input. */
  async inputSchema(): Promise<WorkflowRunInputSchema> {
    return (await this.graph()).inputSchema();
  }

  /** Erod schema + mode for exposing workflow run input to tools (e.g. Coco). */
  async runInputSchemaBundle(): Promise<WorkflowRunInputSchemaBundle> {
    return (await this.inputSchema()).toBundle();
  }

  /**
   * Validates input/output compatibility across workflow edges.
   *
   * For each node with `inputShape`, every incoming edge must come from a source
   * node with a scalar `defaultOutput` that can be inferred and checked against
   * the required input type.
   */
  async validate(
    options?: WorkflowValidateOptions
  ): Promise<WorkflowValidationResult> {
    const nodes = await this.nodes.get();
    const edges = await this.edges.get();
    const nodeById = new Map(nodes.map((node) => [node.id, node] as const));
    const issues: WorkflowValidationIssue[] = [];
    const incomingByNodeId = new Map<string, WorkflowEdge[]>();

    for (const edge of edges) {
      const fromNode = nodeById.get(edge.fromNodeId);
      const toNode = nodeById.get(edge.toNodeId);
      if (fromNode === undefined) {
        issues.push({
          code: 'UnknownNodeReference',
          edgeId: edge.id,
          nodeId: edge.fromNodeId,
          message: `Edge ${edge.id} references missing source node ${edge.fromNodeId}.`,
        });
      }
      if (toNode === undefined) {
        issues.push({
          code: 'UnknownNodeReference',
          edgeId: edge.id,
          nodeId: edge.toNodeId,
          message: `Edge ${edge.id} references missing target node ${edge.toNodeId}.`,
        });
      }
      if (toNode !== undefined) {
        const existing = incomingByNodeId.get(toNode.id) ?? [];
        existing.push(edge);
        incomingByNodeId.set(toNode.id, existing);
      }
    }

    for (const node of nodes) {
      if (node.inputShape === null) {
        continue;
      }
      const parsedInputShape = EraQL.parseTy(node.inputShape);
      if (parsedInputShape.isErr()) {
        issues.push({
          code: 'InvalidInputShape',
          nodeId: node.id,
          inputShape: node.inputShape,
          message: `Node ${node.id} has invalid inputShape: "${node.inputShape}".`,
        });
        continue;
      }
      const incomingEdges = incomingByNodeId.get(node.id) ?? [];
      for (const edge of incomingEdges) {
        const sourceNode = nodeById.get(edge.fromNodeId);
        if (sourceNode === undefined) {
          continue;
        }
        if (
          sourceNode.defaultOutput === null ||
          sourceNode.defaultOutput === undefined
        ) {
          issues.push({
            code: 'MissingIncomingOutput',
            nodeId: node.id,
            sourceNodeId: sourceNode.id,
            edgeId: edge.id,
            message: `Node ${node.id} requires input but source node ${sourceNode.id} has no defaultOutput.`,
          });
          continue;
        }
        if (!Parser.isScalar(sourceNode.defaultOutput)) {
          issues.push({
            code: 'InvalidSourceDefaultOutput',
            nodeId: node.id,
            sourceNodeId: sourceNode.id,
            edgeId: edge.id,
            message: `Source node ${sourceNode.id} defaultOutput is not a valid scalar shape.`,
          });
          continue;
        }
        const inferredOutputShape = Parser.inferType([
          sourceNode.defaultOutput,
        ]);
        if (inferredOutputShape === undefined) {
          issues.push({
            code: 'InvalidSourceDefaultOutput',
            nodeId: node.id,
            sourceNodeId: sourceNode.id,
            edgeId: edge.id,
            message: `Could not infer output shape from source node ${sourceNode.id} defaultOutput.`,
          });
          continue;
        }
        if (
          !TC.implementsTy({
            subject: inferredOutputShape,
            req: parsedInputShape.value,
          })
        ) {
          issues.push({
            code: 'InputOutputMismatch',
            nodeId: node.id,
            sourceNodeId: sourceNode.id,
            edgeId: edge.id,
            inputShape: node.inputShape,
            sourceOutputShape: Ty.displayTy(inferredOutputShape),
            message: `Node ${node.id} inputShape "${
              node.inputShape
            }" is incompatible with source node ${
              sourceNode.id
            } output shape "${Ty.displayTy(inferredOutputShape)}".`,
          });
        }
      }
    }

    const webhookOutputs = options?.webhookTriggerOutputs;
    if (webhookOutputs !== undefined && webhookOutputs.length > 0) {
      const triggers = await this.triggers();
      const triggerBindings = await this.triggerBindings.get();
      const entryNodeIds = new Set(await this.entryNodeIds());

      for (const binding of triggerBindings) {
        if (
          binding.triggerType !== PersistedWorkflowTriggerKind.Webhook ||
          binding.enabled !== true
        ) {
          continue;
        }

        const webhookTrigger = triggers.find(
          (trigger) =>
            trigger.kind === PersistedWorkflowTriggerKind.Webhook &&
            trigger.id === binding.triggerId
        );
        if (webhookTrigger === undefined) {
          continue;
        }

        const outputContext = webhookOutputs.find(
          (candidate) => candidate.triggerId === binding.triggerId
        );
        if (outputContext === undefined) {
          continue;
        }

        for (const node of nodes) {
          if (!entryNodeIds.has(node.id) || node.inputShape === null) {
            continue;
          }

          const parsedInputShape = EraQL.parseTy(node.inputShape);
          if (parsedInputShape.isErr()) {
            continue;
          }

          if (!Parser.isScalar(outputContext.sampleRunInput)) {
            issues.push({
              code: 'MissingWebhookOutput',
              nodeId: node.id,
              triggerId: binding.triggerId,
              message: `Webhook trigger ${binding.triggerId} sample output is not a valid scalar shape for node ${node.id}.`,
            });
            continue;
          }

          const inferredWebhookOutput = Parser.inferType([
            outputContext.sampleRunInput,
          ]);
          if (inferredWebhookOutput === undefined) {
            issues.push({
              code: 'MissingWebhookOutput',
              nodeId: node.id,
              triggerId: binding.triggerId,
              message: `Could not infer webhook output shape for trigger ${binding.triggerId} when validating node ${node.id}.`,
            });
            continue;
          }

          if (
            !providerOutputSatisfiesInput({
              subject: inferredWebhookOutput,
              req: parsedInputShape.value,
            })
          ) {
            issues.push({
              code: 'WebhookInputMismatch',
              nodeId: node.id,
              triggerId: binding.triggerId,
              inputShape: node.inputShape,
              webhookOutputShape: outputContext.runOutputShape,
              message: `Node ${node.id} inputShape "${node.inputShape}" is incompatible with webhook trigger ${binding.triggerId} output shape "${outputContext.runOutputShape}".`,
            });
          }
        }
      }
    }

    return {
      valid: issues.length === 0,
      issues,
    };
  }

  serialize(): SerializedWorkflow {
    return {
      id: this._data.id,
      ownerId: this._data.ownerId,
      ownerOrgId: this._data.ownerOrgId,
      name: this._data.name,
      status: this._data.status,
      idempotencyKey: this._data.idempotencyKey,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
  }

  static parseIdempotencyKeyConfig(
    value: unknown
  ): WorkflowIdempotencyKey | null {
    if (value === null || value === undefined) {
      return null;
    }
    const parsed = parseWorkflowIdempotencyKey(value);
    if (!parsed.ok) {
      return null;
    }
    return parsed.config;
  }

  static fromSerialized(
    serialized: SerializedWorkflow,
    hydration: WorkflowHydration
  ): Workflow {
    const data: WorkflowData = {
      id: serialized.id,
      ownerId: serialized.ownerId,
      ownerOrgId: serialized.ownerOrgId,
      name: serialized.name,
      status: serialized.status,
      idempotencyKey: serialized.idempotencyKey ?? null,
      createdAt: fromIsoString(serialized.createdAt),
      updatedAt: fromIsoString(serialized.updatedAt),
    };
    return new Workflow(data, hydration);
  }

  static fromGraphDefinition(params: {
    id: string;
    ownerId: string;
    ownerOrgId: string;
    name: string;
    status: string;
    serializedNodes: readonly SerializedWorkflowNode[];
    serializedEdges: readonly SerializedWorkflowEdge[];
    cronTrigger?: Omit<DraftCronTriggerParams, 'triggerId' | 'bindingId'>;
    webhookTrigger?: Omit<DraftWebhookTriggerParams, 'triggerId' | 'bindingId'>;
    eventTrigger?: Omit<DraftEventTriggerParams, 'triggerId' | 'bindingId'>;
  }): Workflow {
    const now = new Date();
    const nowIso = toIsoString(now);
    const workflowData: WorkflowData = {
      id: params.id,
      ownerId: params.ownerId,
      ownerOrgId: params.ownerOrgId,
      name: params.name,
      status: params.status,
      idempotencyKey: null,
      createdAt: now,
      updatedAt: now,
    };
    let workflowRef: Workflow | null = null;
    const nodes = params.serializedNodes.map((node) =>
      WorkflowNodeModel.fromSerialized(
        {
          ...node,
          createdAt: node.createdAt ?? nowIso,
          updatedAt: node.updatedAt ?? nowIso,
        },
        { loadWorkflow: async () => workflowRef }
      )
    );
    const edges = params.serializedEdges.map((edge) =>
      WorkflowEdgeModel.fromSerialized({
        ...edge,
        createdAt: edge.createdAt ?? nowIso,
        updatedAt: edge.updatedAt ?? nowIso,
      })
    );
    const triggerBindings: WorkflowTriggerBinding[] = [];
    const triggers: WorkflowTrigger[] = [];

    if (params.cronTrigger !== undefined) {
      const triggerId = v4();
      const bindingId = v4();
      triggers.push(
        WorkflowCronTrigger.fromSerialized({
          id: triggerId,
          ownerId: params.ownerId,
          ownerOrgId: params.ownerOrgId,
          name: params.cronTrigger.name,
          cron: params.cronTrigger.schedule,
          timezone: params.cronTrigger.timezone,
          workflowInput: params.cronTrigger.workflowInput ?? {},
          enabled: params.cronTrigger.enabled ?? true,
          createdAt: nowIso,
          updatedAt: nowIso,
        })
      );
      triggerBindings.push(
        WorkflowTriggerBindingModel.fromSerialized({
          id: bindingId,
          ownerId: params.ownerId,
          ownerOrgId: params.ownerOrgId,
          triggerType: PersistedWorkflowTriggerKind.Cron,
          triggerId,
          workflowId: params.id,
          enabled: params.cronTrigger.enabled ?? true,
          priority: params.cronTrigger.priority ?? 0,
          defaultInput: params.cronTrigger.defaultInput ?? {},
          createdAt: nowIso,
          updatedAt: nowIso,
        })
      );
    }

    if (params.webhookTrigger !== undefined) {
      const triggerId = v4();
      const bindingId = v4();
      triggers.push(
        WorkflowWebhookTrigger.fromSerialized({
          id: triggerId,
          ownerId: params.ownerId,
          ownerOrgId: params.ownerOrgId,
          name: params.webhookTrigger.name ?? 'Webhook trigger',
          secret: params.webhookTrigger.secret,
          enabled: params.webhookTrigger.enabled ?? true,
          createdAt: nowIso,
          updatedAt: nowIso,
        })
      );
      triggerBindings.push(
        WorkflowTriggerBindingModel.fromSerialized({
          id: bindingId,
          ownerId: params.ownerId,
          ownerOrgId: params.ownerOrgId,
          triggerType: PersistedWorkflowTriggerKind.Webhook,
          triggerId,
          workflowId: params.id,
          enabled: params.webhookTrigger.enabled ?? true,
          priority: params.webhookTrigger.priority ?? 0,
          defaultInput: params.webhookTrigger.defaultInput ?? {},
          createdAt: nowIso,
          updatedAt: nowIso,
        })
      );
    }

    if (params.eventTrigger !== undefined) {
      const triggerId = v4();
      const bindingId = v4();
      triggers.push(
        WorkflowEventTrigger.fromSerialized({
          id: triggerId,
          ownerId: params.ownerId,
          ownerOrgId: params.ownerOrgId,
          name: params.eventTrigger.name,
          eventName: params.eventTrigger.eventName,
          enabled: params.eventTrigger.enabled ?? true,
          createdAt: nowIso,
          updatedAt: nowIso,
        })
      );
      triggerBindings.push(
        WorkflowTriggerBindingModel.fromSerialized({
          id: bindingId,
          ownerId: params.ownerId,
          ownerOrgId: params.ownerOrgId,
          triggerType: PersistedWorkflowTriggerKind.Event,
          triggerId,
          workflowId: params.id,
          enabled: params.eventTrigger.enabled ?? true,
          priority: params.eventTrigger.priority ?? 0,
          defaultInput: params.eventTrigger.defaultInput ?? {},
          createdAt: nowIso,
          updatedAt: nowIso,
        })
      );
    }

    const draftCollections: DraftCollections = {
      nodes: [...nodes],
      edges: [...edges],
      triggerBindings,
      triggers,
    };
    const workflow = new Workflow(
      workflowData,
      {
        loadNodes: async () => draftCollections.nodes,
        loadEdges: async () => draftCollections.edges,
        loadTriggerBindings: async () => draftCollections.triggerBindings,
        loadTriggers: async () => draftCollections.triggers,
        preloadedNodes: draftCollections.nodes,
        preloadedEdges: draftCollections.edges,
        preloadedTriggerBindings: draftCollections.triggerBindings,
        preloadedTriggers: draftCollections.triggers,
      },
      draftCollections
    );
    workflowRef = workflow;
    return workflow;
  }

  static createDraft(params: {
    id?: string;
    ownerId: string;
    ownerOrgId: string;
    name: string;
    status?: string;
    idempotencyKey?: WorkflowIdempotencyKey | null;
    createdAt?: Date;
    updatedAt?: Date;
  }): Workflow {
    const now = new Date();
    const data: WorkflowData = {
      id: params.id ?? v4(),
      ownerId: params.ownerId,
      ownerOrgId: params.ownerOrgId,
      name: params.name,
      status: params.status ?? 'active',
      idempotencyKey: params.idempotencyKey ?? null,
      createdAt: params.createdAt ?? now,
      updatedAt: params.updatedAt ?? now,
    };
    const draftCollections: DraftCollections = {
      nodes: [],
      edges: [],
      triggerBindings: [],
      triggers: [],
    };
    return new Workflow(
      data,
      {
        loadNodes: async () => draftCollections.nodes,
        loadEdges: async () => draftCollections.edges,
        loadTriggerBindings: async () => draftCollections.triggerBindings,
        loadTriggers: async () => draftCollections.triggers,
        preloadedNodes: draftCollections.nodes,
        preloadedEdges: draftCollections.edges,
        preloadedTriggerBindings: draftCollections.triggerBindings,
        preloadedTriggers: draftCollections.triggers,
      },
      draftCollections
    );
  }

  static fromPersisted(snapshot: PersistedWorkflowSnapshot): Workflow {
    let workflowRef: Workflow | null = null;
    const nodes = snapshot.nodes.map((node) =>
      WorkflowNodeModel.fromSerialized(node, {
        loadWorkflow: async () => workflowRef,
      })
    );
    const edges = snapshot.edges.map((edge) =>
      WorkflowEdgeModel.fromSerialized(edge)
    );
    const triggerBindings = snapshot.triggerBindings.map((binding) =>
      WorkflowTriggerBindingModel.fromSerialized(binding)
    );
    const triggers = snapshot.triggers.map((trigger) => {
      switch (trigger.kind) {
        case PersistedWorkflowTriggerKind.Cron:
          return WorkflowCronTrigger.fromSerialized(trigger.trigger);
        case PersistedWorkflowTriggerKind.Webhook:
          return WorkflowWebhookTrigger.fromSerialized(trigger.trigger);
        case PersistedWorkflowTriggerKind.Event:
          return WorkflowEventTrigger.fromSerialized(trigger.trigger);
        case PersistedWorkflowTriggerKind.Dataset:
          return WorkflowDatasetTrigger.fromSerialized(trigger.trigger);
        default:
          return Assert.unreachable(trigger);
      }
    });
    const workflow = Workflow.fromSerialized(snapshot.workflow, {
      loadNodes: async () => nodes,
      loadEdges: async () => edges,
      loadTriggerBindings: async () => triggerBindings,
      loadTriggers: async () => triggers,
      preloadedNodes: nodes,
      preloadedEdges: edges,
      preloadedTriggerBindings: triggerBindings,
      preloadedTriggers: triggers,
    });
    workflowRef = workflow;
    return workflow;
  }

  static fromWorkflowResource(resource: WorkflowResource): Workflow | null {
    const graph = resource.graph;
    if (graph === undefined) {
      return null;
    }
    return Workflow.fromPersisted({
      workflow: {
        id: resource.id,
        ownerId: resource.ownerId,
        ownerOrgId: resource.orgId,
        name: resource.name,
        status: resource.workflowStatus ?? 'active',
        idempotencyKey: null,
        createdAt: toIsoString(resource.createdAt),
        updatedAt: toIsoString(resource.updatedAt),
      },
      nodes: graph.nodes.map((node) => ({
        id: node.id,
        ownerId: node.ownerId,
        ownerOrgId: node.ownerOrgId,
        workflowId: node.workflowId,
        type: node.type as WorkflowNodeType,
        data: node.data ?? ({} as JsonValue),
        inputShape: node.inputShape,
        defaultOutput: node.defaultOutput ?? null,
        inputFromNodeId: node.inputFromNodeId ?? null,
        createdAt: node.createdAt,
        updatedAt: node.updatedAt,
      })),
      edges: graph.edges.map((edge) => ({
        id: edge.id,
        ownerId: edge.ownerId,
        ownerOrgId: edge.ownerOrgId,
        workflowId: edge.workflowId,
        fromNodeId: edge.fromNodeId,
        toNodeId: edge.toNodeId,
        condition: edge.condition ?? {},
        createdAt: edge.createdAt,
        updatedAt: edge.updatedAt,
      })),
      triggerBindings: graph.triggerBindings.map((binding) => ({
        id: binding.id,
        ownerId: binding.ownerId,
        ownerOrgId: binding.ownerOrgId,
        triggerType: binding.triggerType,
        triggerId: binding.triggerId,
        workflowId: binding.workflowId,
        enabled: binding.enabled,
        priority: binding.priority,
        defaultInput: binding.defaultInput ?? {},
        createdAt: binding.createdAt,
        updatedAt: binding.updatedAt,
      })),
      triggers: graph.triggers.map((trigger) => {
        switch (trigger.kind) {
          case PersistedWorkflowTriggerKind.Cron:
            return {
              kind: PersistedWorkflowTriggerKind.Cron,
              trigger: trigger.trigger,
            };
          case PersistedWorkflowTriggerKind.Webhook:
            return {
              kind: PersistedWorkflowTriggerKind.Webhook,
              trigger: {
                ...trigger.trigger,
                // Secrets are intentionally excluded from API workflow graph payloads.
                // Rehydrate with an empty placeholder because workflow reconstruction
                // for FE checks (e.g. scheduled-agent detection) does not depend on it.
                secret: '',
              },
            };
          case PersistedWorkflowTriggerKind.Event:
            return {
              kind: PersistedWorkflowTriggerKind.Event,
              trigger: trigger.trigger,
            };
          case PersistedWorkflowTriggerKind.Dataset:
            return {
              kind: PersistedWorkflowTriggerKind.Dataset,
              trigger: trigger.trigger,
            };
          default:
            return Assert.unreachable(trigger);
        }
      }),
    });
  }

  private requireDraftCollections(): DraftCollections {
    if (this.draftCollections === undefined) {
      throw new Error(
        'Workflow mutating builder methods require Workflow.createDraft(...)'
      );
    }
    return this.draftCollections;
  }

  private pushNode(node: WorkflowNode): WorkflowNode {
    const draftCollections = this.requireDraftCollections();
    draftCollections.nodes.push(node);
    return node;
  }

  node(params: DraftNodeParams): WorkflowNode {
    const now = new Date();
    const node = WorkflowNodeModel.fromSerialized(
      {
        id: params.id ?? v4(),
        ownerId: this.ownerId,
        ownerOrgId: this.ownerOrgId,
        workflowId: this.id,
        type: params.type as WorkflowNodeType,
        data: params.data ?? ({} as JsonValue),
        inputShape: params.inputShape ?? null,
        defaultOutput: params.defaultOutput ?? (null as JsonValue | null),
        inputFromNodeId: null,
        createdAt: toIsoString(now),
        updatedAt: toIsoString(now),
      },
      { loadWorkflow: async () => this, preloadedWorkflow: this }
    );
    return this.pushNode(node);
  }

  agentNode(params: DraftAgentNodeParams): WorkflowNode {
    return this.node({
      id: params.id,
      type: WorkflowNodeType.Agent,
      data: {
        ...(params.data ?? {}),
        agentId: params.agentId,
      },
      inputShape: params.inputShape ?? null,
      defaultOutput: params.defaultOutput ?? null,
    });
  }

  forEachNode(params: DraftForEachNodeParams): WorkflowNode {
    if (params.node === undefined) {
      return this.node({
        id: params.id,
        type: WorkflowNodeType.ForEach,
        data: {
          ...(params.data ?? {}),
          ...(params.itemsPath !== undefined && {
            itemsPath: params.itemsPath,
          }),
          ...(params.sourceRunNodeId !== undefined && {
            sourceRunNodeId: params.sourceRunNodeId,
          }),
        },
        inputShape: params.inputShape ?? null,
        defaultOutput: params.defaultOutput ?? null,
      });
    }

    // Create the loop body first so we can stamp childNodeId on the for-each
    // node (flat graph + edge; runtime fan-out keys off childNodeId).
    const child = this.appendForEachChildNode(params.node);
    const forEachNode = this.node({
      id: params.id,
      type: WorkflowNodeType.ForEach,
      data: {
        ...(params.data ?? {}),
        ...(params.itemsPath !== undefined && { itemsPath: params.itemsPath }),
        ...(params.sourceRunNodeId !== undefined && {
          sourceRunNodeId: params.sourceRunNodeId,
        }),
        childNodeId: child.id,
      },
      inputShape: params.inputShape ?? null,
      defaultOutput: params.defaultOutput ?? null,
    });
    this.edge({
      fromNodeId: forEachNode.id,
      toNodeId: child.id,
    });
    return forEachNode;
  }

  private appendForEachChildNode(
    params: DraftForEachChildNodeParams
  ): WorkflowNode {
    switch (params.type) {
      case WorkflowNodeType.Agent:
        return this.agentNode(params);
      case WorkflowNodeType.AdHocAgent:
        return this.adHocAgentNode(params);
      case WorkflowNodeType.CompleteToolCall:
        return this.completeToolCallNode(params);
      default:
        return Assert.unreachable(params);
    }
  }

  adHocAgentNode(params: DraftAdHocAgentNodeParams): WorkflowNode {
    return this.node({
      id: params.id,
      type: WorkflowNodeType.AdHocAgent,
      data: {
        ...(params.data ?? {}),
        model: params.model,
        ...(params.system !== undefined && { system: params.system }),
        ...(params.toolKeys !== undefined && { toolKeys: params.toolKeys }),
        ...(params.mode !== undefined && { mode: params.mode }),
        ...(params.agent !== undefined && { agent: params.agent }),
        ...(params.agentId !== undefined && { agentId: params.agentId }),
        ...(params.outputShape !== undefined && {
          outputShape: params.outputShape,
        }),
        ...(params.conversationId !== undefined && {
          conversationId: params.conversationId,
        }),
        ...(params.parentConversationId !== undefined && {
          parentConversationId: params.parentConversationId,
        }),
        ...(params.parentToolCallId !== undefined && {
          parentToolCallId: params.parentToolCallId,
        }),
      },
      inputShape: params.inputShape ?? null,
      defaultOutput: params.defaultOutput ?? null,
    });
  }

  completeToolCallNode(params: DraftCompleteToolCallNodeParams): WorkflowNode {
    return this.node({
      id: params.id,
      type: WorkflowNodeType.CompleteToolCall,
      data: {
        ...(params.data ?? {}),
        parentConversationId: params.parentConversationId,
        toolCallId: params.toolCallId,
        parentModel: params.parentModel,
        ...(params.mode !== undefined && { mode: params.mode }),
      },
      inputShape: params.inputShape ?? null,
      defaultOutput: params.defaultOutput ?? null,
    });
  }

  toolCallNode(params: DraftToolCallNodeParams): WorkflowNode {
    return this.node({
      id: params.id,
      type: WorkflowNodeType.ToolCall,
      data: {
        ...(params.data ?? {}),
        key: params.key,
        params: params.params,
        ...(params.paramBindings !== undefined && {
          paramBindings: params.paramBindings,
        }),
        ...(params.interruptableTool !== undefined && {
          interruptableTool: params.interruptableTool,
        }),
      },
      inputShape: params.inputShape ?? null,
      defaultOutput: params.defaultOutput ?? null,
    });
  }

  triggerCron(params: DraftCronTriggerParams): Workflow {
    const draftCollections = this.requireDraftCollections();
    const now = new Date();
    const triggerId = params.triggerId ?? v4();
    const bindingId = params.bindingId ?? v4();
    const trigger = WorkflowCronTrigger.fromSerialized({
      id: triggerId,
      ownerId: this.ownerId,
      ownerOrgId: this.ownerOrgId,
      name: params.name,
      cron: params.schedule,
      timezone: params.timezone,
      workflowInput: params.workflowInput ?? {},
      enabled: params.enabled ?? true,
      createdAt: toIsoString(now),
      updatedAt: toIsoString(now),
    });
    draftCollections.triggers.push(trigger);
    draftCollections.triggerBindings.push(
      WorkflowTriggerBindingModel.fromSerialized({
        id: bindingId,
        ownerId: this.ownerId,
        ownerOrgId: this.ownerOrgId,
        triggerType: PersistedWorkflowTriggerKind.Cron,
        triggerId,
        workflowId: this.id,
        enabled: params.enabled ?? true,
        priority: params.priority ?? 0,
        defaultInput: params.defaultInput ?? {},
        createdAt: toIsoString(now),
        updatedAt: toIsoString(now),
      })
    );
    return this;
  }

  triggerWebhook(params: DraftWebhookTriggerParams): Workflow {
    const draftCollections = this.requireDraftCollections();
    const now = new Date();
    const triggerId = params.triggerId ?? v4();
    const bindingId = params.bindingId ?? v4();
    const trigger = WorkflowWebhookTrigger.fromSerialized({
      id: triggerId,
      ownerId: this.ownerId,
      ownerOrgId: this.ownerOrgId,
      name: params.name ?? 'Webhook trigger',
      secret: params.secret,
      providerKey: params.providerKey,
      verificationConfig: params.verificationConfig,
      enabled: params.enabled ?? true,
      createdAt: toIsoString(now),
      updatedAt: toIsoString(now),
    });
    draftCollections.triggers.push(trigger);
    draftCollections.triggerBindings.push(
      WorkflowTriggerBindingModel.fromSerialized({
        id: bindingId,
        ownerId: this.ownerId,
        ownerOrgId: this.ownerOrgId,
        triggerType: PersistedWorkflowTriggerKind.Webhook,
        triggerId,
        workflowId: this.id,
        enabled: params.enabled ?? true,
        priority: params.priority ?? 0,
        defaultInput: params.defaultInput ?? {},
        createdAt: toIsoString(now),
        updatedAt: toIsoString(now),
      })
    );
    return this;
  }

  triggerEvent(params: DraftEventTriggerParams): Workflow {
    const draftCollections = this.requireDraftCollections();
    const now = new Date();
    const triggerId = params.triggerId ?? v4();
    const bindingId = params.bindingId ?? v4();
    const trigger = WorkflowEventTrigger.fromSerialized({
      id: triggerId,
      ownerId: this.ownerId,
      ownerOrgId: this.ownerOrgId,
      name: params.name,
      eventName: params.eventName,
      enabled: params.enabled ?? true,
      createdAt: toIsoString(now),
      updatedAt: toIsoString(now),
    });
    draftCollections.triggers.push(trigger);
    draftCollections.triggerBindings.push(
      WorkflowTriggerBindingModel.fromSerialized({
        id: bindingId,
        ownerId: this.ownerId,
        ownerOrgId: this.ownerOrgId,
        triggerType: PersistedWorkflowTriggerKind.Event,
        triggerId,
        workflowId: this.id,
        enabled: params.enabled ?? true,
        priority: params.priority ?? 0,
        defaultInput: params.defaultInput ?? {},
        createdAt: toIsoString(now),
        updatedAt: toIsoString(now),
      })
    );
    return this;
  }

  edge(params: DraftEdgeParams): Workflow {
    const draftCollections = this.requireDraftCollections();
    const now = new Date();
    draftCollections.edges.push(
      WorkflowEdgeModel.fromSerialized({
        id: params.id ?? v4(),
        ownerId: this.ownerId,
        ownerOrgId: this.ownerOrgId,
        workflowId: this.id,
        fromNodeId: params.fromNodeId,
        toNodeId: params.toNodeId,
        condition: params.condition ?? {},
        createdAt: toIsoString(now),
        updatedAt: toIsoString(now),
      })
    );
    return this;
  }

  async toPersisted(): Promise<PersistedWorkflowSnapshot> {
    const [nodes, edges, triggerBindings, triggers] = await Promise.all([
      this.nodes.get(),
      this.edges.get(),
      this.triggerBindings.get(),
      this.triggers(),
    ]);
    const serializedTriggers: PersistedWorkflowTriggerSnapshot[] = [];
    for (const trigger of triggers) {
      if (trigger.isCronTrigger()) {
        serializedTriggers.push({
          kind: PersistedWorkflowTriggerKind.Cron,
          trigger: trigger.serialize(),
        });
        continue;
      }
      if (trigger.isWebhookTrigger()) {
        serializedTriggers.push({
          kind: PersistedWorkflowTriggerKind.Webhook,
          trigger: trigger.serialize(),
        });
        continue;
      }
      if (trigger.isEventTrigger()) {
        serializedTriggers.push({
          kind: PersistedWorkflowTriggerKind.Event,
          trigger: trigger.serialize(),
        });
        continue;
      }
      if (trigger.isDatasetTrigger()) {
        serializedTriggers.push({
          kind: PersistedWorkflowTriggerKind.Dataset,
          trigger: trigger.serialize(),
        });
        continue;
      }
      // These are `this is X` guards over an abstract base, not a discriminated
      // union, so the compiler cannot check this list for exhaustiveness — an
      // `Assert.unreachable(trigger as never)` here only *looked* safe. Name the
      // kind instead, so a missing branch fails legibly rather than as
      // "Unreachable, got: {...}".
      throw new Error(
        `Cannot persist workflow trigger of unhandled kind "${trigger.kind}" (id ${trigger.id})`
      );
    }
    return {
      workflow: this.serialize(),
      nodes: nodes.map((node) => node.serialize()),
      edges: edges.map((edge) => edge.serialize()),
      triggerBindings: triggerBindings.map((binding) => binding.serialize()),
      triggers: serializedTriggers,
    };
  }
}

export async function resolveEmptyWorkflowNodes(): Promise<
  readonly WorkflowNode[]
> {
  return [];
}

export async function resolveEmptyWorkflowEdges(): Promise<
  readonly WorkflowEdge[]
> {
  return [];
}

export async function resolveEmptyWorkflowTriggerBindings(): Promise<
  readonly WorkflowTriggerBinding[]
> {
  return [];
}

export async function resolveEmptyWorkflowTriggers(): Promise<
  readonly WorkflowTrigger[]
> {
  return [];
}
